import { useMemo } from 'react';
import { UseGrossIRRSummaryParams, UseGrossIRRSummaryReturn } from './useGrossIRRSummary.props';
import { TRANSACTION_TYPE } from '../data/constants';
import { calculateXIRR, CashFlowForIRR } from '../utils/irrCalculations';

export const useGrossIRRSummary = ({
  fundList,
  cashFlows = [],
  ownershipOptions = [],
}: UseGrossIRRSummaryParams): UseGrossIRRSummaryReturn => {
  // Build fund ID to name mapping from ownershipOptions (which has the display labels)
  // or fall back to fundList
  const fundIdToName = useMemo(() => {
    const mapping: Record<number, string> = {};

    // First, try to get names from ownershipOptions (more reliable for display)
    ownershipOptions.forEach(option => {
      if (option.type === 'fund' && option.value) {
        // Extract fund ID from value like "fund-123"
        const match = option.value.match(/^fund-(\d+)$/);
        if (match) {
          const fundId = parseInt(match[1], 10);
          mapping[fundId] = option.label;
        }
      }
    });

    // Fall back to fundList for any missing IDs
    if (fundList && Array.isArray(fundList)) {
      fundList.forEach(fund => {
        if (typeof fund === 'undefined' || !fund.id || !fund.name) {
          return;
        }
        if (!mapping[fund.id]) {
          mapping[fund.id] = fund.name;
        }
      });
    }

    return mapping;
  }, [ownershipOptions, fundList]);

  // Calculate IRR directly from cashFlows
  // cashFlows contains BOTH virtual entries (from FundOwnershipDetails + AllocationFundConclusions)
  // AND manual entries (from CashFlowLedgerItem model)
  const { fundsGrossIrr, firmGrossIrr } = useMemo(() => {
    const flowsByFund: Record<number, CashFlowForIRR[]> = {};
    const allFlows: CashFlowForIRR[] = [];

    cashFlows.forEach(item => {
      // Extract fund ID
      let fundId: number | null = null;
      if (item.owner_fund) {
        fundId = item.owner_fund;
      } else if (typeof item.owner === 'string' && item.owner.startsWith('fund-')) {
        const match = item.owner.match(/^fund-(\d+)$/);
        if (match) {
          fundId = parseInt(match[1], 10);
        }
      }

      // Skip entries without a fund owner (company-owned entries don't contribute to fund IRR)
      if (fundId === null) {
        return;
      }

      const date = new Date(item.date_of_transaction);
      if (Number.isNaN(date.getTime())) {
        return;
      }

      let amount = Number(item.amount) || 0;

      // For virtual entries, ensure correct signs based on transaction type
      if (item.is_virtual) {
        if (item.transaction_type === TRANSACTION_TYPE.INVESTMENT) {
          // Investments should be negative (outflows)
          amount = -Math.abs(amount);
        } else if (
          item.transaction_type === TRANSACTION_TYPE.REALIZED_PROCEEDS ||
          item.transaction_type === TRANSACTION_TYPE.UNREALIZED_VALUE
        ) {
          // Realized proceeds and unrealized value should be positive (inflows)
          amount = Math.abs(amount);
        }
        // For other virtual types, keep as-is
      }
      // For manual entries, use the amount as entered by the user (they control the sign)

      const flow: CashFlowForIRR = { date, amount };

      if (!flowsByFund[fundId]) {
        flowsByFund[fundId] = [];
      }
      flowsByFund[fundId].push(flow);
      allFlows.push(flow);
    });

    // Calculate Fund IRRs
    const calculatedFundsIrr: Record<string, number | null> = {};
    Object.entries(flowsByFund).forEach(([fundId, flows]) => {
      calculatedFundsIrr[fundId] = calculateXIRR(flows);
    });

    // Calculate Firm IRR (Deal Level - all funds combined for this company)
    const calculatedFirmIrr = calculateXIRR(allFlows);

    return {
      fundsGrossIrr: calculatedFundsIrr,
      firmGrossIrr: calculatedFirmIrr,
    };
  }, [cashFlows]);

  return {
    fundsGrossIrr,
    firmGrossIrr,
    fundIdToName,
    isLoading: false,
    error: null,
  };
};
